import type { EarningsLedgerEntry } from '../schemas/agent.js';

/**
 * Sprint 95 — pure helpers powering agent + recruiter motivation surfaces
 * under the one-time signup-commission model.
 *
 * Three groups:
 *
 *   1. `summariseLedgerByMonth` — fold the ledger into per-month
 *      buckets so the home page can render a "last N months" bar
 *      chart + month-over-month deltas. No I/O, no Firebase types —
 *      caller passes already-loaded entries.
 *
 *   2. `forecastNextMonthSen` — simple-but-honest projection of next
 *      month's earnings based on the agent's signup velocity (avg
 *      signups per month × avg commission per signup over a trailing
 *      window). Falls back to `'none'` confidence when there isn't
 *      enough history. Surfaces should label it "based on your recent
 *      signup pace" so an agent doesn't read it as guaranteed income.
 *
 *   3. `computeMilestones` — derive achieved-vs-pending milestones
 *      from totals. Real-event-triggered (only flips on actual
 *      ledger / backref state); the existing dopamine memory bans
 *      fake celebrations.
 *
 * Time scoping uses `nowMs` passed in (not `Date.now()`) so tests
 * pin to a known instant.
 */

// ── classification ─────────────────────────────────────────────────────

export type LedgerEntryClass =
  | 'signup' // one-time agent signup commission (1 month of subscription)
  | 'first_invoice_bonus' // signup_fee + activation_fee share (when plan has fees)
  | 'recruiter_signup' // recruiter's flat-per-downstream-signup commission
  | 'recruiter_bonus' // recruiter's slice of downstream first-invoice fees
  | 'clawback'
  | 'adjustment'
  | 'payout'
  | 'other';

/**
 * Distinguish the kind of one-time earning on EACH ledger entry. The
 * agent-pwa /earnings page pivots its kind-pill label off this so the
 * agent sees "Signup commission" vs "First-time bonus" instead of two
 * opaque "Accrued" pills.
 */
export function classifyLedgerEntry(entry: EarningsLedgerEntry): LedgerEntryClass {
  if (entry.kind === 'clawed_back') return 'clawback';
  if (entry.kind === 'adjustment') return 'adjustment';
  if (entry.kind === 'paid') return 'payout';
  // 'accrued' branch — look at basis.shareKind to disambiguate.
  const basis = entry.basis as Record<string, unknown>;
  const shareKind = typeof basis['shareKind'] === 'string' ? (basis['shareKind'] as string) : null;
  switch (shareKind) {
    case 'agent_signup_commission':
      return 'signup';
    case 'first_invoice_onboarding':
      return 'first_invoice_bonus';
    case 'recruiter_signup_commission':
      return 'recruiter_signup';
    case 'recruiter_first_invoice':
      return 'recruiter_bonus';
    default:
      // Legacy entries written before Sprint 95 (recurring/recruiter
      // recurring) — treat as signup so they still count in totals.
      return 'signup';
  }
}

// ── monthly aggregation ────────────────────────────────────────────────

export interface MonthBucket {
  /** `YYYY-MM` for stable sorting + storage. */
  readonly monthKey: string;
  /** Human label (e.g., "May 2026"). */
  readonly label: string;
  /** ms-since-epoch at the start of the month — drives chart positioning. */
  readonly startMs: number;
  /** Net sum (signup + bonus + adjustments + clawbacks; excludes payouts). */
  readonly netSen: number;
  /**
   * Signup commissions earned this month — the headline number. Field
   * kept as `recurringSen` for backward chart-component compatibility
   * (it's the foundation stack in the bar chart). Semantics changed in
   * Sprint 95: this is now "signup commissions" not "recurring".
   */
  readonly recurringSen: number;
  /** One-shot first-invoice fee shares (signup_fee + activation_fee). */
  readonly bonusSen: number;
  /** Negative-amount clawback total for the month (always 0 or negative). */
  readonly clawbackSen: number;
  /** Count of signup accrual entries — "X signups this month". */
  readonly accrualCount: number;
}

export function summariseLedgerByMonth(
  entries: readonly EarningsLedgerEntry[],
  nowMs: number,
  monthsBack = 6,
): MonthBucket[] {
  // Seed an empty bucket for each month in the window so chart bars
  // include a zero-entry month rather than skipping it.
  const buckets = new Map<string, MonthBucket>();
  const now = new Date(nowMs);
  for (let i = monthsBack - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = monthKeyOf(d);
    buckets.set(key, {
      monthKey: key,
      label: d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
      startMs: d.getTime(),
      netSen: 0,
      recurringSen: 0,
      bonusSen: 0,
      clawbackSen: 0,
      accrualCount: 0,
    });
  }

  for (const e of entries) {
    const ms = tsMs(e.accruedAt);
    if (!ms) continue;
    const key = monthKeyOf(new Date(ms));
    const bucket = buckets.get(key);
    if (!bucket) continue; // outside the window — skipped
    const cls = classifyLedgerEntry(e);
    if (cls === 'payout') continue; // payouts are debits to balance, not earnings
    const next: MonthBucket = {
      ...bucket,
      netSen: bucket.netSen + e.amount,
      recurringSen:
        cls === 'signup' || cls === 'recruiter_signup'
          ? bucket.recurringSen + e.amount
          : bucket.recurringSen,
      bonusSen:
        cls === 'first_invoice_bonus' || cls === 'recruiter_bonus'
          ? bucket.bonusSen + e.amount
          : bucket.bonusSen,
      clawbackSen: cls === 'clawback' ? bucket.clawbackSen + e.amount : bucket.clawbackSen,
      accrualCount:
        cls === 'signup' || cls === 'recruiter_signup'
          ? bucket.accrualCount + 1
          : bucket.accrualCount,
    };
    buckets.set(key, next);
  }

  return Array.from(buckets.values()).sort((a, b) => a.startMs - b.startMs);
}

// ── projection ─────────────────────────────────────────────────────────

export interface NextMonthForecast {
  /** Forecasted total earnings next month, in sen. Floor (operator-friendly). */
  readonly recurringSen: number;
  /** Confidence label — surfaces use this to pick wording. */
  readonly confidence: 'none' | 'low' | 'medium' | 'high';
  /** Number of completed months used in the projection. */
  readonly basedOnMonths: number;
  /** Trailing-N-month average earnings (drives the projection). */
  readonly trailingAvgRecurringSen: number;
  /** Average signups per month over the trailing window. */
  readonly avgSignupsPerMonth: number;
  /** Average commission per signup over the trailing window (sen). */
  readonly avgSenPerSignup: number;
}

/**
 * Signup-velocity projection: average the trailing N COMPLETED months
 * (default 3) where there was at least one signup. Excludes the current
 * in-flight month so a slow start doesn't yank the forecast down
 * mid-cycle. Confidence reflects how many completed months actually had
 * data.
 *
 * Under the one-time commission model, "earnings next month" is a
 * projection of NEW signups × avg-commission-per-signup, NOT of
 * recurring revenue.
 */
export function forecastNextMonthSen(
  buckets: readonly MonthBucket[],
  nowMs: number,
  trailingMonths = 3,
): NextMonthForecast {
  const currentKey = monthKeyOf(new Date(nowMs));
  // Only completed months that ACTUALLY had signups count. Averaging
  // across empty months would deflate the forecast AND inflate
  // confidence (it'd say "3 months of data!" when reality was "1 paid
  // month + 2 empties" — misleading).
  const window = buckets
    .filter((b) => b.monthKey !== currentKey && b.accrualCount > 0)
    .slice(-trailingMonths);

  if (window.length === 0) {
    return {
      recurringSen: 0,
      confidence: 'none',
      basedOnMonths: 0,
      trailingAvgRecurringSen: 0,
      avgSignupsPerMonth: 0,
      avgSenPerSignup: 0,
    };
  }
  const sumEarnings = window.reduce((acc, b) => acc + b.recurringSen, 0);
  const sumSignups = window.reduce((acc, b) => acc + b.accrualCount, 0);
  const avgEarnings = Math.floor(sumEarnings / window.length);
  const avgSignups = sumSignups / window.length;
  const avgPerSignup = sumSignups > 0 ? Math.floor(sumEarnings / sumSignups) : 0;
  const confidence: NextMonthForecast['confidence'] =
    window.length >= 3 ? 'high' : window.length === 2 ? 'medium' : 'low';

  return {
    recurringSen: avgEarnings,
    confidence,
    basedOnMonths: window.length,
    trailingAvgRecurringSen: avgEarnings,
    avgSignupsPerMonth: avgSignups,
    avgSenPerSignup: avgPerSignup,
  };
}

// ── streak ─────────────────────────────────────────────────────────────

/**
 * Returns the count of CONSECUTIVE completed months (ending with the
 * most-recent completed one) where the agent had any signup earnings.
 * "Signed up an operator every month for 3 months running" → 3.
 */
export function earningsStreakMonths(buckets: readonly MonthBucket[], nowMs: number): number {
  const currentKey = monthKeyOf(new Date(nowMs));
  const completed = buckets.filter((b) => b.monthKey !== currentKey);
  let streak = 0;
  for (let i = completed.length - 1; i >= 0; i -= 1) {
    const b = completed[i];
    if (b && b.recurringSen + b.bonusSen > 0) streak += 1;
    else break;
  }
  return streak;
}

// ── milestones ─────────────────────────────────────────────────────────

export interface MilestoneInput {
  readonly activeOperatorCount: number;
  readonly lifetimeOperatorCount: number;
  readonly lifetimeEarningsSen: number;
  readonly earningsStreakMonths: number;
}

export interface Milestone {
  readonly id: string;
  /**
   * i18n key for the milestone title. This module is pure (no React / no
   * i18n runtime — it also runs in Functions + tests), so it returns a
   * stable catalog KEY rather than a resolved English string. The
   * agent-pwa consumers resolve it via `useI18n()`. Copy lives in the
   * `agent.milestones.*` namespace of the i18n catalog (all 4 locales).
   */
  readonly titleKey: string;
  /** i18n key for the milestone body/description (same namespace). */
  readonly bodyKey: string;
  /** Interpolation vars for the title/body keys, when the copy needs any. */
  readonly vars?: Readonly<Record<string, string | number>>;
  readonly unlocked: boolean;
  /** Progress toward unlock (0..1). Drives the progress ring on locked tiles. */
  readonly progress: number;
}

/**
 * Real-event-triggered milestones — every entry's `unlocked` flag is
 * derived from actual state (operator count + ledger totals + streak),
 * never faked. Surfaces should celebrate ONLY transitions
 * (from unlocked=false to unlocked=true).
 *
 * Titles/bodies are returned as i18n keys (`agent.milestones.*`), not
 * English strings — the caller resolves them with `useI18n()`. This keeps
 * the milestone COMPUTATION (thresholds, ordering, unlocked/progress)
 * identical across every locale; only the display copy is localised.
 */
export function computeMilestones(input: MilestoneInput): Milestone[] {
  const { lifetimeOperatorCount, lifetimeEarningsSen, earningsStreakMonths: streak } = input;

  function fract(now: number, target: number): number {
    if (target <= 0) return 1;
    return Math.max(0, Math.min(1, now / target));
  }

  return [
    {
      id: 'first-signup',
      titleKey: 'agent.milestones.firstSignup.title',
      bodyKey: 'agent.milestones.firstSignup.body',
      unlocked: lifetimeOperatorCount >= 1,
      progress: fract(lifetimeOperatorCount, 1),
    },
    {
      id: 'three-signups',
      titleKey: 'agent.milestones.threeSignups.title',
      bodyKey: 'agent.milestones.threeSignups.body',
      unlocked: lifetimeOperatorCount >= 3,
      progress: fract(lifetimeOperatorCount, 3),
    },
    {
      id: 'ten-signups',
      titleKey: 'agent.milestones.tenSignups.title',
      bodyKey: 'agent.milestones.tenSignups.body',
      unlocked: lifetimeOperatorCount >= 10,
      progress: fract(lifetimeOperatorCount, 10),
    },
    {
      id: 'rm1k-lifetime',
      titleKey: 'agent.milestones.rm1kLifetime.title',
      bodyKey: 'agent.milestones.rm1kLifetime.body',
      unlocked: lifetimeEarningsSen >= 100_000,
      progress: fract(lifetimeEarningsSen, 100_000),
    },
    {
      id: 'rm10k-lifetime',
      titleKey: 'agent.milestones.rm10kLifetime.title',
      bodyKey: 'agent.milestones.rm10kLifetime.body',
      unlocked: lifetimeEarningsSen >= 1_000_000,
      progress: fract(lifetimeEarningsSen, 1_000_000),
    },
    {
      id: 'three-month-streak',
      titleKey: 'agent.milestones.threeMonthStreak.title',
      bodyKey: 'agent.milestones.threeMonthStreak.body',
      unlocked: streak >= 3,
      progress: fract(streak, 3),
    },
  ];
}

// ── helpers ────────────────────────────────────────────────────────────

function monthKeyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function tsMs(ts: { toMillis?: () => number; seconds?: number } | unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const obj = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') {
    return obj.seconds * 1000 + Math.floor((obj.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}
